import axios from "axios";
import CryptoJS from "crypto-js";

// API base URL from environment variable
// REACT_APP_API_URL should be set in .env file (e.g., http://localhost:2023)
// If not set, defaults to empty string (relative URLs for same-origin requests)
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

// API Key from environment variable
const API_KEY = process.env.REACT_APP_API_KEY || "";

// Generate a random 32-character hex string (16 bytes = 32 hex characters)
const generateNonceString = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
};

// Generate HMAC signature for admin requests
const generateHmacSignature = (
  secret: string,
  timestamp: number,
  payload: string,
): string => {
  const message = `${timestamp}:${payload}`;
  return CryptoJS.HmacSHA256(message, secret).toString(CryptoJS.enc.Hex);
};

export const getApiUrl = (endpoint: string): string => {
  // If endpoint already starts with /api, use it as is
  if (endpoint.startsWith("/api")) {
    return API_BASE_URL ? `${API_BASE_URL}${endpoint}` : endpoint;
  }
  // Otherwise, prepend /api
  const fullEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return API_BASE_URL
    ? `${API_BASE_URL}/api${fullEndpoint}`
    : `/api${fullEndpoint}`;
};

export const API_ENDPOINTS = {
  LOGIN: "/api/Public/auth/login",
  LOGOUT_ADMIN: "/api/Administrator/auth/logout",
  LOGOUT_MERCHANT: "/api/Merchant/lobby/auth/logout",
  REFRESH_TOKEN_ADMIN: "/api/Administrator/auth/refresh-token",
  REFRESH_TOKEN_MERCHANT: "/api/Merchant/lobby/auth/refresh-token",
  // Admin endpoints
  ADMIN_GET_USERS: "/api/Administrator/users",
  ADMIN_GET_PENDING_USERS: "/api/Administrator/pending-users",
  ADMIN_DELETE_PENDING_USER: "/api/Administrator/pending-users/{pendingUserId}",
  ADMIN_GET_USER_DETAILS: "/api/Administrator/user/{userId}/{userType}",
  ADMIN_RESET_USER_2FA: "/api/Administrator/user/{userId}/{userType}/reset-2fa",
  ADMIN_GET_ACCOUNT_PROFILE: "/api/Administrator/account/profile",
  ADMIN_UPDATE_ACCOUNT_PROFILE: "/api/Administrator/account/profile/update",
  ADMIN_CHANGE_PASSWORD: "/api/Administrator/auth/change-password",
  ADMIN_RESET_2FA: "/api/Administrator/auth/reset-2fa",
  ADMIN_GET_SETTINGS: "/api/Administrator/app-settings",
  ADMIN_GET_SETTING: "/api/Administrator/app-settings/{key}",
  ADMIN_UPDATE_SETTING: "/api/Administrator/app-settings/{key}/update",
  ADMIN_GET_RSA_PUBLIC_KEY: "/api/Administrator/app-settings/rsa-public-key",
  ADMIN_UPDATE_RSA_KEY_PAIR:
    "/api/Administrator/app-settings/rsa-key-pair/update",
  ADMIN_SYNC_RSA_KEY_TO_WATCHER: "/api/Administrator/app-settings/sync-rsa-key-to-watcher",
  ADMIN_GET_WEBHOOK_IPS: "/api/Administrator/app-settings/webhook-ips",
  ADMIN_UPDATE_WEBHOOK_IPS:
    "/api/Administrator/app-settings/webhook-ips/update",
  ADMIN_GET_DISCOVERED_WATCHER_IPS:
    "/api/Administrator/app-settings/discovered-watcher-ips",
  ADMIN_APPROVE_DISCOVERED_IPS:
    "/api/Administrator/app-settings/approve-discovered-ips",
  // Birthday Email endpoints
  ADMIN_GET_BIRTHDAY_EMAIL_SETTINGS:
    "/api/Administrator/birthday-emails/settings",
  ADMIN_UPDATE_BIRTHDAY_EMAIL_SETTINGS:
    "/api/Administrator/birthday-emails/settings/update",
  ADMIN_SEND_BIRTHDAY_EMAILS_NOW: "/api/Administrator/birthday-emails/send-now",
  // Internal Email endpoints
  ADMIN_GET_INTERNAL_EMAILS: "/api/Administrator/internal-emails",
  ADMIN_SEND_INTERNAL_EMAIL: "/api/Administrator/internal-emails/send",
  ADMIN_SEND_BULK_EMAIL: "/api/Administrator/internal-emails/send-bulk",
  ADMIN_GET_WEBHOOK_LOGS: "/api/Administrator/webhooks",
  ADMIN_GET_WEBHOOK_LOG: "/api/Administrator/webhooks/{webhookLogId}",
  ADMIN_RETRY_WEBHOOK: "/api/Administrator/webhooks/{webhookLogId}/retry",
  ADMIN_DELETE_WEBHOOK_LOG: "/api/Administrator/webhooks/{webhookLogId}",
  ADMIN_GET_MERCHANT_WEBHOOK_PREFERENCES:
    "/api/Administrator/webhooks/preferences/{merchantCompanyId}",
  ADMIN_GET_WEBHOOK_EVENT_TYPES: "/api/Administrator/webhooks/event-types",
  ADMIN_GET_DASHBOARD_STATS: "/api/Administrator/dashboard/stats",
  ADMIN_GET_WALLET_ASSIGNMENTS_BY_MERCHANT:
    "/api/Administrator/dashboard/charts/wallet-assignments",
  ADMIN_GET_SWEEP_TRENDS: "/api/Administrator/dashboard/charts/sweep-trends",
  ADMIN_GET_WEBHOOK_STATS: "/api/Administrator/dashboard/charts/webhook-stats",
  ADMIN_GET_WALLET_DELIVERY_TRENDS:
    "/api/Administrator/dashboard/charts/wallet-delivery-trends",
  ADMIN_GET_TOP_MERCHANTS_BY_WALLETS:
    "/api/Administrator/dashboard/charts/top-merchants",
  ADMIN_GET_ACTION_ITEMS: "/api/Administrator/dashboard/action-items",
  ADMIN_LOGOUT_ALL_ADMINS: "/api/Administrator/auth/logout-all-admins",
  ADMIN_LOGOUT_ALL_MERCHANTS: "/api/Administrator/auth/logout-all-merchants",
  // Companies
  ADMIN_GET_COMPANIES: "/api/Administrator/companies",
  ADMIN_GET_COMPANY_DETAILS: "/api/Administrator/companies/{companyId}",
  ADMIN_DELETE_COMPANY: "/api/Administrator/companies/delete/{companyId}",
  ADMIN_DELETE_COMPANY_FOREVER: "/api/Administrator/companies/delete-forever/{companyId}",
  ADMIN_UPDATE_COMPANY_STATUS:
    "/api/Administrator/companies/{companyId}/update-status",
  ADMIN_ACTIVATE_MERCHANT: "/api/Administrator/companies/{companyId}/activate",
  ADMIN_ASSIGN_GAS_WALLET:
    "/api/Administrator/companies/{companyId}/assign-gas-wallet",
  ADMIN_ASSIGN_INTERNAL_WALLET:
    "/api/Administrator/companies/{companyId}/assign-internal-wallet",
  ADMIN_GET_GAS_WALLET: "/api/Administrator/companies/{companyId}/gas-wallet",
  ADMIN_GET_INTERNAL_WALLET:
    "/api/Administrator/companies/{companyId}/internal-wallet",
  ADMIN_GET_DEFAULT_MERCHANT: "/api/Administrator/default-merchant",
  ADMIN_UPDATE_DEFAULT_MERCHANT_CUSTODIAL_WALLETS:
    "/api/Administrator/default-merchant/custodial-wallets",
  ADMIN_CREATE_MERCHANT_WITH_COMPANY:
    "/api/Administrator/merchant/create-with-company",
  // Subscription Plans - GET uses /all, POST uses /create
  ADMIN_ALL_SUBSCRIPTION_PLANS: "/api/Administrator/subscription-plans/all",
  ADMIN_GET_SUBSCRIPTION_PLAN: "/api/Administrator/subscription-plans/{planId}",
  ADMIN_CREATE_SUBSCRIPTION_PLAN:
    "/api/Administrator/subscription-plans/create",
  ADMIN_UPDATE_SUBSCRIPTION_PLAN:
    "/api/Administrator/subscription-plans/{planId}/update",
  ADMIN_BULK_UPDATE_SUBSCRIPTION_PLANS:
    "/api/Administrator/subscription-plans/bulk-update",
  ADMIN_DELETE_SUBSCRIPTION_PLAN:
    "/api/Administrator/subscription-plans/{planId}/delete",
  // Merchant Subscription - GET, POST (create), PUT (update) with unique paths
  ADMIN_GET_MERCHANT_SUBSCRIPTION:
    "/api/Administrator/merchants/{companyId}/subscription",
  ADMIN_CREATE_MERCHANT_SUBSCRIPTION:
    "/api/Administrator/merchants/{companyId}/subscription/create",
  ADMIN_UPDATE_MERCHANT_SUBSCRIPTION:
    "/api/Administrator/merchants/{companyId}/subscription/update",
  ADMIN_CANCEL_MERCHANT_SUBSCRIPTION:
    "/api/Administrator/merchants/{companyId}/subscription/cancel",
  ADMIN_UPDATE_MERCHANT_PRICING:
    "/api/Administrator/merchants/{companyId}/subscription/pricing",
  // Merchant Credits - GET and POST (add) with unique paths
  ADMIN_GET_MERCHANT_CREDITS:
    "/api/Administrator/merchants/{companyId}/credits",
  ADMIN_ADD_MERCHANT_CREDITS:
    "/api/Administrator/merchants/{companyId}/credits/add",
  ADMIN_GET_MERCHANT_INTERNAL_WALLET:
    "/api/Administrator/merchants/{companyId}/internal-wallet",
  ADMIN_GET_MERCHANT_RESOURCE_USAGE:
    "/api/Administrator/merchants/{companyId}/resource-usage",
  ADMIN_GET_CREDIT_TOKENS: "/api/Administrator/credit-tokens",
  ADMIN_CREATE_CREDIT_TOKEN: "/api/Administrator/credit-tokens/create",
  ADMIN_GET_CREDIT_TOKEN: "/api/Administrator/credit-tokens/{tokenId}",
  ADMIN_ACTIVATE_CREDIT_TOKEN:
    "/api/Administrator/credit-tokens/{tokenId}/activate",
  ADMIN_DEACTIVATE_CREDIT_TOKEN:
    "/api/Administrator/credit-tokens/{tokenId}/deactivate",
  ADMIN_RESYNC_COMPANY_TO_WATCHER:
    "/api/Administrator/companies/{companyId}/resync-to-watcher",
  // VAT Configuration Management
  ADMIN_GET_VAT_CONFIGURATIONS: "/api/Administrator/billing/vat-configurations",
  ADMIN_GET_VAT_CONFIGURATION:
    "/api/Administrator/billing/vat-configurations/{id}",
  ADMIN_CREATE_VAT_CONFIGURATION:
    "/api/Administrator/billing/vat-configurations",
  ADMIN_UPDATE_VAT_CONFIGURATION:
    "/api/Administrator/billing/vat-configurations/{id}",
  ADMIN_DELETE_VAT_CONFIGURATION:
    "/api/Administrator/billing/vat-configurations/{id}",
  // Currencies
  ADMIN_GET_CURRENCIES: "/api/Administrator/currencies",
  ADMIN_GET_CURRENCY: "/api/Administrator/currency/{id}",
  ADMIN_CREATE_CURRENCY: "/api/Administrator/currency/create",
  ADMIN_UPDATE_CURRENCY: "/api/Administrator/currency/update/{id}",
  ADMIN_RESYNC_CURRENCY_TO_WATCHER:
    "/api/Administrator/currency/{id}/resync-to-watcher",
  // Blockchain Networks
  ADMIN_GET_BLOCKCHAIN_NETWORKS: "/api/Administrator/blockchain-networks",
  ADMIN_GET_BLOCKCHAIN_NETWORK: "/api/Administrator/blockchain-network/{id}",
  ADMIN_CREATE_BLOCKCHAIN_NETWORK:
    "/api/Administrator/blockchain-network/create",
  ADMIN_UPDATE_BLOCKCHAIN_NETWORK:
    "/api/Administrator/blockchain-network/update/{id}",
  ADMIN_RESYNC_BLOCKCHAIN_NETWORK_TO_WATCHER:
    "/api/Administrator/blockchain-network/{id}/resync-to-watcher",
  // Contract Addresses
  ADMIN_GET_CONTRACT_ADDRESSES: "/api/Administrator/contract-addresses",
  ADMIN_GET_CONTRACT_ADDRESS: "/api/Administrator/contract-address/{id}",
  ADMIN_CREATE_CONTRACT_ADDRESS: "/api/Administrator/contract-address/create",
  ADMIN_UPDATE_CONTRACT_ADDRESS:
    "/api/Administrator/contract-address/update/{id}",
  ADMIN_DELETE_CONTRACT_ADDRESS:
    "/api/Administrator/contract-address/delete/{id}",
  ADMIN_RESYNC_CONTRACT_ADDRESS_TO_WATCHER:
    "/api/Administrator/contract-address/{id}/resync-to-watcher",
  // RPC Endpoints
  ADMIN_GET_RPC_ENDPOINTS: "/api/Administrator/settings/rpc",
  ADMIN_GET_RPC_ENDPOINT: "/api/Administrator/settings/rpc/{id}",
  ADMIN_CREATE_RPC_ENDPOINT: "/api/Administrator/settings/rpc/create",
  ADMIN_UPDATE_RPC_ENDPOINT: "/api/Administrator/settings/rpc/update/{id}",
  ADMIN_DELETE_RPC_ENDPOINT: "/api/Administrator/settings/rpc/delete/{id}",
  ADMIN_RESYNC_RPC_ENDPOINT_TO_WATCHER:
    "/api/Administrator/settings/rpc/{id}/resync-to-watcher",
  ADMIN_PING_RPC_ENDPOINT: "/api/Administrator/settings/rpc/{id}/ping",
  // Email Templates
  ADMIN_EMAIL_TEMPLATES_GET: "/api/Administrator/email-templates",
  ADMIN_EMAIL_TEMPLATES_VALIDATE: "/api/Administrator/email-templates/validate",
  ADMIN_GET_EMAIL_ANALYTICS: "/api/Administrator/emails/analytics",
  ADMIN_GET_EMAIL_LOGS: "/api/Administrator/email-logs",
  ADMIN_GET_EMAIL_ANALYTICS_BY_MESSAGE_ID:
    "/api/Administrator/email-logs/{messageId}/analytics",
  ADMIN_GET_LOGIN_HISTORY: "/api/Administrator/auth/login-history",
  ADMIN_RESEND_VERIFICATION_EMAIL:
    "/api/Administrator/auth/account/resend-verification-email",
  // User Management
  ADMIN_DELETE_USER: "/api/Administrator/user/delete/{userId}/{userType}",
  ADMIN_DISABLE_USER_LOGIN:
    "/api/Administrator/user/{userId}/{userType}/disable-login",
  ADMIN_ENABLE_USER_LOGIN:
    "/api/Administrator/user/{userId}/{userType}/enable-login",
  ADMIN_UPDATE_ADMINISTRATOR_ROLE: "/api/Administrator/user/{userId}/role",
  ADMIN_CREATE_ADMIN_ACCOUNT: "/api/Administrator/auth/account",
  // Merchant endpoints
  MERCHANT_GET_ACCOUNT_PROFILE: "/api/Merchant/lobby/account/profile",
  MERCHANT_UPDATE_ACCOUNT_PROFILE: "/api/Merchant/lobby/account/profile/update",
  MERCHANT_CHANGE_PASSWORD: "/api/Merchant/lobby/auth/change-password",
  MERCHANT_RESET_2FA: "/api/Merchant/lobby/auth/reset-2fa",
  MERCHANT_GET_COMPANY_PROFILE: "/api/Merchant/lobby/company/profile",
  MERCHANT_UPDATE_COMPANY_PROFILE: "/api/Merchant/lobby/company/profile/update",
  MERCHANT_VALIDATE_INTEGRATION:
    "/api/Merchant/lobby/company/validate-integration",
  MERCHANT_GET_KYC_STATUS: "/api/Merchant/lobby/kyc/status",
  MERCHANT_GET_KYC_VERIFICATION_URL: "/api/Merchant/lobby/kyc/verification-url",
  ADMIN_GET_COMPANY_KYC: "/api/Administrator/companies/{companyId}/kyc",
  ADMIN_UPDATE_COMPANY_KYC: "/api/Administrator/companies/{companyId}/kyc/update",
  MERCHANT_GET_STAFF: "/api/Merchant/lobby/company/staff",
  MERCHANT_GET_PENDING_STAFF: "/api/Merchant/lobby/company/staff/pending",
  MERCHANT_GET_STAFF_DETAILS: "/api/Merchant/lobby/company/staff/{staffId}",
  MERCHANT_RESEND_VERIFICATION_EMAIL:
    "/api/Merchant/lobby/company/staff/resend-verification-email",
  MERCHANT_GET_LOGIN_HISTORY: "/api/Merchant/lobby/auth/login-history",
  MERCHANT_GET_PUBLIC_KEY: "/api/Merchant/lobby/company/public-key",
  MERCHANT_UPDATE_PUBLIC_KEY: "/api/Merchant/lobby/company/public-key/update",
  MERCHANT_REMOVE_PUBLIC_KEY: "/api/Merchant/lobby/company/public-key/remove",
  MERCHANT_GET_SWITCH_WALLET_PUBLIC_KEY:
    "/api/Merchant/lobby/switch-wallet-public-key",
  MERCHANT_GET_WEBHOOK_IPS: "/api/Merchant/lobby/webhook-ips",
  MERCHANT_SEND_INTERNAL_EMAIL_TO_MERCHANT:
    "/api/Merchant/lobby/internal-emails/send",
  MERCHANT_GET_INTERNAL_EMAILS: "/api/Merchant/lobby/internal-emails",
  MERCHANT_GET_API_IP_WHITELIST: "/api/Merchant/lobby/api-ip-whitelist",
  MERCHANT_CREATE_API_IP_WHITELIST:
    "/api/Merchant/lobby/api-ip-whitelist/create",
  MERCHANT_GET_API_IP_WHITELIST_ENTRY:
    "/api/Merchant/lobby/api-ip-whitelist/{id}",
  MERCHANT_UPDATE_API_IP_WHITELIST_ENTRY:
    "/api/Merchant/lobby/api-ip-whitelist/{id}/update",
  MERCHANT_DELETE_API_IP_WHITELIST_ENTRY:
    "/api/Merchant/lobby/api-ip-whitelist/{id}/delete",
  MERCHANT_UPDATE_MERCHANT_ROLE: "/api/Merchant/lobby/company/staff/{userId}/role",
  // Public endpoints for merchant
  PUBLIC_GET_BLOCKCHAIN_NETWORKS: "/api/Public/blockchain-networks",
  PUBLIC_GET_CURRENCIES: "/api/Public/currencies",
  PUBLIC_GET_CONTRACT_ADDRESSES: "/api/Public/contract-addresses",
  PUBLIC_GET_ENUMS: "/api/Public/enums",
  PUBLIC_GET_SUBSCRIPTION_PLANS: "/api/Public/subscription-plans",
  // MPC Service endpoints
  // Note: Nodes and orchestrator data come from GET_MPC_SETTINGS (database, updated via webhooks)
  // Direct HTTP calls to nodes/Aso Rock are not possible
  ADMIN_GET_MPC_SETTINGS: "/api/Administrator/mpc/settings",
  ADMIN_UPDATE_MPC_SETTINGS: "/api/Administrator/mpc/settings/update",
  // Aso Rock Cron Jobs endpoints
  ADMIN_ASO_ROCK_CRON_JOBS: "/api/Administrator/asorock/cron-jobs",
  ADMIN_ASO_ROCK_CRON_JOB: "/api/Administrator/asorock/cron-job/{jobName}",
  ADMIN_ASO_ROCK_CRON_JOB_UPDATE:
    "/api/Administrator/asorock/cron-job/{jobName}/update",
  // Multi-Party Wallets endpoints
  ADMIN_GET_WALLETS: "/api/Administrator/wallets",
  ADMIN_GET_WALLET: "/api/Administrator/wallets/{walletId}",
  ADMIN_GET_WALLET_ONCHAIN_BALANCES:
    "/api/Administrator/wallets/{walletId}/on-chain-wallet-balances",
  ADMIN_GET_WALLET_MERCHANTS: "/api/Administrator/wallets/merchants",
  ADMIN_SYNC_WALLET_TO_WATCHER:
    "/api/Administrator/wallets/{walletId}/sync-to-watcher",
  // Sweep List endpoints (Admin)
  ADMIN_GET_SWEEP_LIST: "/api/Administrator/sweep-list",
  ADMIN_GET_SWEEP_LIST_ITEM: "/api/Administrator/sweep-list/{id}",
  ADMIN_VALIDATE_SWEEP_LIST_ITEM: "/api/Administrator/sweep-list/validate",
  ADMIN_CREATE_SWEEP_LIST_ITEM: "/api/Administrator/sweep-list/create",
  ADMIN_UPDATE_SWEEP_LIST_ITEM: "/api/Administrator/sweep-list/{id}/update",
  ADMIN_DELETE_SWEEP_LIST_ITEM: "/api/Administrator/sweep-list/{id}/delete",
  // Merchant endpoints
  MERCHANT_GET_WALLETS: "/api/Merchant/lobby/wallets",
  MERCHANT_GET_WALLET: "/api/Merchant/lobby/wallets/{walletId}",
  MERCHANT_GET_WALLET_ONCHAIN_BALANCES:
    "/api/Merchant/lobby/wallets/{walletId}/on-chain-wallet-balances",
  MERCHANT_GENERATE_WALLET: "/api/Merchant/lobby/wallets/generate",
  MERCHANT_GET_GAS_WALLET: "/api/Merchant/lobby/gas-wallet",
  // Sweep List endpoints (Merchant)
  MERCHANT_GET_SWEEP_LIST: "/api/Merchant/lobby/sweep-list",
  MERCHANT_GET_SWEEP_LIST_ITEM: "/api/Merchant/lobby/sweep-list/{id}",
  MERCHANT_VALIDATE_SWEEP_LIST_ITEM: "/api/Merchant/lobby/sweep-list/validate",
  MERCHANT_CREATE_SWEEP_LIST_ITEM: "/api/Merchant/lobby/sweep-list/create",
  MERCHANT_UPDATE_SWEEP_LIST_ITEM: "/api/Merchant/lobby/sweep-list/{id}/update",
  MERCHANT_CREATE_UNKNOWN_TOKEN_SWEEP_REQUEST:
    "/api/Merchant/lobby/merchant-update-requests/unknown-token",
  MERCHANT_DELETE_SWEEP_LIST_ITEM: "/api/Merchant/lobby/sweep-list/{id}/delete",
  // Merchant Update Requests (Merchant)
  MERCHANT_GET_UPDATE_REQUESTS: "/api/Merchant/lobby/merchant-update-requests",
  MERCHANT_GET_UPDATE_REQUEST:
    "/api/Merchant/lobby/merchant-update-requests/{id}",
  // Admin Critical Notifications endpoints
  ADMIN_GET_CRITICAL_NOTIFICATIONS: "/api/Administrator/critical-notifications",
  ADMIN_GET_CRITICAL_NOTIFICATION:
    "/api/Administrator/critical-notifications/{id}",
  ADMIN_CREATE_CRITICAL_NOTIFICATION:
    "/api/Administrator/critical-notifications/create",
  ADMIN_MARK_NOTIFICATION_AS_SEEN:
    "/api/Administrator/critical-notifications/{id}/mark-seen",
  ADMIN_MARK_ALL_NOTIFICATIONS_AS_SEEN:
    "/api/Administrator/critical-notifications/mark-all-seen",
  ADMIN_UPDATE_NOTIFICATION_BUG_STATUS:
    "/api/Administrator/critical-notifications/{id}/bug-status",
  ADMIN_DELETE_CRITICAL_NOTIFICATION:
    "/api/Administrator/critical-notifications/{id}/delete",
  ADMIN_DELETE_ALL_NOTIFICATIONS:
    "/api/Administrator/critical-notifications/delete-all-admin",
  ADMIN_DELETE_ALL_NOTIFICATIONS_FOREVER:
    "/api/Administrator/critical-notifications/delete-all-admin-forever",
  ADMIN_GET_NOTIFICATION_STATS:
    "/api/Administrator/critical-notifications/stats",
  // Merchant Update Requests (Admin)
  ADMIN_GET_MERCHANT_UPDATE_REQUESTS:
    "/api/Administrator/merchant-update-requests",
  ADMIN_APPROVE_MERCHANT_UPDATE_REQUEST:
    "/api/Administrator/merchant-update-requests/{id}/approve",
  ADMIN_REJECT_MERCHANT_UPDATE_REQUEST:
    "/api/Administrator/merchant-update-requests/{id}/reject",
  ADMIN_CREATE_UNKNOWN_TOKEN_SWEEP_REQUEST:
    "/api/Administrator/merchant-update-requests/unknown-token",
  // Merchant Supported Resources (Admin View)
  ADMIN_GET_MERCHANT_SUPPORTED_BLOCKCHAIN_NETWORKS:
    "/api/Administrator/companies/{companyId}/supported/blockchain-networks",
  ADMIN_GET_MERCHANT_SUPPORTED_CURRENCIES:
    "/api/Administrator/companies/{companyId}/supported/currencies",
  ADMIN_GET_MERCHANT_SUPPORTED_CONTRACT_ADDRESSES:
    "/api/Administrator/companies/{companyId}/supported/contract-addresses",
  // Merchant Critical Notifications endpoints
  MERCHANT_GET_CRITICAL_NOTIFICATIONS:
    "/api/Merchant/lobby/critical-notifications",
  MERCHANT_GET_CRITICAL_NOTIFICATION:
    "/api/Merchant/lobby/critical-notifications/{id}",
  MERCHANT_CREATE_CRITICAL_NOTIFICATION:
    "/api/Merchant/lobby/critical-notifications/create",
  MERCHANT_MARK_NOTIFICATION_AS_SEEN:
    "/api/Merchant/lobby/critical-notifications/{id}/mark-seen",
  MERCHANT_MARK_ALL_NOTIFICATIONS_AS_SEEN:
    "/api/Merchant/lobby/critical-notifications/mark-all-seen",
  MERCHANT_DELETE_CRITICAL_NOTIFICATION:
    "/api/Merchant/lobby/critical-notifications/{id}/delete",
  MERCHANT_DELETE_ALL_NOTIFICATIONS:
    "/api/Merchant/lobby/critical-notifications/delete-all",
  // Merchant Supported Resources endpoints
  MERCHANT_GET_SUPPORTED_BLOCKCHAIN_NETWORKS:
    "/api/Merchant/lobby/supported/blockchain-networks",
  MERCHANT_TOGGLE_SUPPORTED_BLOCKCHAIN_NETWORK:
    "/api/Merchant/lobby/supported/blockchain-networks/{blockchainNetworkId}/toggle",
  MERCHANT_GET_SUPPORTED_CURRENCIES: "/api/Merchant/lobby/supported/currencies",
  MERCHANT_TOGGLE_SUPPORTED_CURRENCY:
    "/api/Merchant/lobby/supported/currencies/{currencyId}/toggle",
  MERCHANT_GET_SUPPORTED_CONTRACT_ADDRESSES:
    "/api/Merchant/lobby/supported/contract-addresses",
  MERCHANT_TOGGLE_SUPPORTED_CONTRACT_ADDRESS:
    "/api/Merchant/lobby/supported/contract-addresses/{contractAddressId}/toggle",
  // Merchant Webhook Logs endpoints
  MERCHANT_GET_WEBHOOK_LOGS: "/api/Merchant/lobby/webhooks",
  MERCHANT_GET_WEBHOOK_LOG: "/api/Merchant/lobby/webhooks/{webhookLogId}",
  MERCHANT_RETRY_WEBHOOK: "/api/Merchant/lobby/webhooks/{webhookLogId}/retry",
  MERCHANT_GET_WEBHOOK_PREFERENCES: "/api/Merchant/lobby/webhooks/preferences",
  MERCHANT_UPDATE_WEBHOOK_PREFERENCES:
    "/api/Merchant/lobby/webhooks/preferences/update",
  MERCHANT_GET_WEBHOOK_EVENT_TYPES: "/api/Merchant/lobby/webhooks/event-types",
  // Merchant Dashboard Statistics endpoint
  MERCHANT_GET_DASHBOARD_STATS: "/api/Merchant/lobby/dashboard/stats",
  MERCHANT_GET_SWEEP_STATUS_DISTRIBUTION:
    "/api/Merchant/lobby/dashboard/charts/sweep-status-distribution",
  MERCHANT_GET_SWEEP_TRENDS:
    "/api/Merchant/lobby/dashboard/charts/sweep-trends",
  MERCHANT_GET_WEBHOOK_STATS:
    "/api/Merchant/lobby/dashboard/charts/webhook-stats",
  MERCHANT_GET_WALLET_TRENDS:
    "/api/Merchant/lobby/dashboard/charts/wallet-trends",
  MERCHANT_GET_CURRENCY_DISTRIBUTION_IN_SWEEPS:
    "/api/Merchant/lobby/dashboard/charts/currency-distribution",
  MERCHANT_GET_ACTION_ITEMS: "/api/Merchant/lobby/dashboard/action-items",
  // Merchant Billing endpoints
  MERCHANT_GET_SUBSCRIPTION: "/api/Merchant/lobby/billing/subscription",
  MERCHANT_GET_RESOURCE_USAGE: "/api/Merchant/lobby/billing/resource-usage",
  MERCHANT_GET_CREDIT_BALANCE: "/api/Merchant/lobby/billing/credits",
  MERCHANT_GET_CREDIT_TRANSACTIONS:
    "/api/Merchant/lobby/billing/credits/transactions",
  MERCHANT_REDEEM_CREDIT_TOKEN:
    "/api/Merchant/lobby/billing/credits/redeem-token",
  MERCHANT_GET_INTERNAL_WALLET: "/api/Merchant/lobby/billing/internal-wallet",
  MERCHANT_GET_INTERNAL_WALLET_TRANSACTIONS:
    "/api/Merchant/lobby/billing/internal-wallet/transactions",
  MERCHANT_GET_FUNDING_WALLET_ADDRESS:
    "/api/Merchant/lobby/billing/funding-wallet-address",
  MERCHANT_GET_INVOICES: "/api/Merchant/lobby/billing/invoices",
  MERCHANT_GET_INVOICE: "/api/Merchant/lobby/billing/invoices/{invoiceId}",
  ADMIN_GET_MONTHLY_REVENUE_REPORT:
    "/api/Administrator/billing/reports/monthly-revenue",
  ADMIN_GET_FULL_BILLING_REPORT: "/api/Administrator/billing/reports/full",
  ADMIN_GET_ALL_INVOICES: "/api/Administrator/merchants/invoices",
  ADMIN_GET_MERCHANT_INVOICES:
    "/api/Administrator/merchants/{merchantId}/invoices",
  ADMIN_GET_MERCHANT_INVOICE:
    "/api/Administrator/merchants/{merchantId}/invoices/{invoiceId}",
  ADMIN_MARK_INVOICE_PAID:
    "/api/Administrator/merchants/{merchantId}/invoices/{invoiceId}/mark-paid",
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "x-api-key": API_KEY,
  },
});

// --- Global API loading tracker (for buttons/spinners) ---
type ApiLoadingListener = (count: number) => void;
let apiLoadingCount = 0;
const apiLoadingListeners = new Set<ApiLoadingListener>();

const notifyApiLoading = () => {
  apiLoadingListeners.forEach((listener) => listener(apiLoadingCount));
};

const incrementApiLoading = () => {
  apiLoadingCount += 1;
  notifyApiLoading();
};

const decrementApiLoading = () => {
  apiLoadingCount = Math.max(0, apiLoadingCount - 1);
  notifyApiLoading();
};

export const subscribeToApiLoading = (listener: ApiLoadingListener) => {
  apiLoadingListeners.add(listener);
  return () => apiLoadingListeners.delete(listener);
};

export const getApiLoadingCount = () => apiLoadingCount;

// Request interceptor to add x-nonce-string, Authorization, and HMAC headers to all requests
apiClient.interceptors.request.use(
  (config) => {
    incrementApiLoading();
    // Generate a new nonce string for each request
    config.headers["x-nonce-string"] = generateNonceString();

    // Add Authorization header if accessToken is available
    const accessToken = localStorage.getItem("accessToken");
    const accessSecret = localStorage.getItem("accessSecret");

    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    // Add HMAC signature for authenticated admin/merchant requests
    if (accessSecret && accessToken) {
      const timestamp = Date.now();
      const isGetRequest = config.method?.toLowerCase() === "get";

      // Use the exact payload that axios will send - don't modify it
      // axios will serialize config.data using JSON.stringify, so we do the same
      const payload = isGetRequest ? "{}" : JSON.stringify(config.data || {});

      // Debug logging - remove after fixing
      if (config.url?.includes("sweep-list/create")) {
        console.log("HMAC Debug - Payload string:", payload);
        console.log("HMAC Debug - Config data:", config.data);
        console.log("HMAC Debug - Timestamp:", timestamp);
      }

      const hmacSignature = generateHmacSignature(
        accessSecret,
        timestamp,
        payload,
      );
      config.headers["x-hmac-signature"] = hmacSignature;
      config.headers["x-request-timestamp"] = timestamp.toString();
    }

    return config;
  },
  (error) => {
    incrementApiLoading();
    decrementApiLoading();
    return Promise.reject(error);
  },
);

// Track whether a token refresh is already in progress to avoid concurrent attempts
let isRefreshing = false;
// Queue of requests waiting for the refresh to complete
let refreshSubscribers: Array<(success: boolean) => void> = [];

const onRefreshComplete = (success: boolean) => {
  refreshSubscribers.forEach((cb) => cb(success));
  refreshSubscribers = [];
};

const waitForRefresh = (): Promise<boolean> =>
  new Promise((resolve) => {
    refreshSubscribers.push(resolve);
  });

// Force logout: clear all auth data and redirect to login
const forceLogout = () => {
  const userType = localStorage.getItem("userType") || "Administrator";

  localStorage.removeItem("accessToken");
  localStorage.removeItem("accessSecret");
  localStorage.removeItem("userType");
  localStorage.removeItem("sessionExpiryTime");

  if (window.location.pathname !== "/") {
    const currentRoute =
      window.location.pathname +
      window.location.search +
      window.location.hash;
    const continueParam = encodeURIComponent(currentRoute);
    window.location.href = `/?userType=${userType}&continue=${continueParam}`;
  }
};

// Attempt to refresh the session token before giving up
const attemptTokenRefresh = async (): Promise<boolean> => {
  const accessToken = localStorage.getItem("accessToken");
  const accessSecret = localStorage.getItem("accessSecret");
  const userType = localStorage.getItem("userType") || "Administrator";

  if (!accessToken || !accessSecret) return false;

  const endpoint =
    userType === "Merchant"
      ? API_ENDPOINTS.REFRESH_TOKEN_MERCHANT
      : API_ENDPOINTS.REFRESH_TOKEN_ADMIN;

  try {
    const timestamp = Date.now();
    const payload = "{}";
    const hmacSignature = CryptoJS.HmacSHA256(
      `${timestamp}:${payload}`,
      accessSecret,
    ).toString(CryptoJS.enc.Hex);

    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    const nonce = Array.from(array, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");

    const response = await axios.post(
      getApiUrl(endpoint),
      {},
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
          Authorization: `Bearer ${accessToken}`,
          "x-hmac-signature": hmacSignature,
          "x-request-timestamp": timestamp.toString(),
          "x-nonce-string": nonce,
        },
      },
    );

    if (response.data?.status === "success" && response.data?.data) {
      const data = response.data.data;
      if (data.accessToken) localStorage.setItem("accessToken", data.accessToken);
      if (data.accessSecret) localStorage.setItem("accessSecret", data.accessSecret);
      if (data.sessionExpiryTime) {
        // Import dynamically to avoid circular dependency issues
        const { parseSessionExpiryTime } = await import("./sessionUtils");
        const expiryTimestamp = parseSessionExpiryTime(data.sessionExpiryTime);
        localStorage.setItem("sessionExpiryTime", expiryTimestamp.toString());
      }
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

// Response interceptor to handle 401/403 errors — try refresh first, then logout
apiClient.interceptors.response.use(
  (response) => {
    decrementApiLoading();
    return response;
  },
  async (error) => {
    decrementApiLoading();
    const status = error.response?.status || error.response?.statusCode;
    const originalRequest = error.config;

    // Handle 401/403 errors (token expired / access secret missing)
    if ((status === 401 || status === 403) && !originalRequest._retried) {
      originalRequest._retried = true;

      let refreshed: boolean;

      if (!isRefreshing) {
        // First failing request triggers the refresh
        isRefreshing = true;
        refreshed = await attemptTokenRefresh();
        isRefreshing = false;
        onRefreshComplete(refreshed);
      } else {
        // Other requests wait for the in-progress refresh to finish
        refreshed = await waitForRefresh();
      }

      if (refreshed) {
        // Retry the original request with new tokens and a fresh nonce
        const newAccessToken = localStorage.getItem("accessToken");
        const newAccessSecret = localStorage.getItem("accessSecret");
        if (newAccessToken) {
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        }
        // Generate a fresh nonce for the retry (old nonce is already consumed in Redis)
        originalRequest.headers["x-nonce-string"] = generateNonceString();
        if (newAccessSecret && newAccessToken) {
          const timestamp = Date.now();
          const isGetRequest = originalRequest.method?.toLowerCase() === "get";
          // originalRequest.data may already be a serialized string (axios serializes it before sending).
          // Using JSON.stringify on a string would double-encode it and break HMAC verification.
          const rawData = originalRequest.data;
          const payload = isGetRequest ? "{}" : (typeof rawData === "string" ? rawData : JSON.stringify(rawData || {}));
          const hmacSignature = generateHmacSignature(newAccessSecret, timestamp, payload);
          originalRequest.headers["x-hmac-signature"] = hmacSignature;
          originalRequest.headers["x-request-timestamp"] = timestamp.toString();
        }
        return apiClient(originalRequest);
      }

      // Refresh failed — force logout
      forceLogout();
    }

    return Promise.reject(error);
  },
);
